import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import useOnFetch from './useOnFetch';

/**
 * @typedef {Object} BaseQueryFetchConfig
 * @property {Object} [params]
 * @property {Object} [payload]
 * @property {'POST' | 'GET'} [method]
 * @property {(data: Object) => Object} [onFetchSuccess]
 * @property {(error: Object) => void} [onFetchError]
 * @property {() => void} [onBeforeFetch]
 * @property {() => void} [onFinally]
 * @property {(payload: Object, params: Object) => boolean} [enabled]
 * @property {boolean} [retry]
 * @property {number} [cacheTime=0] - default is 0 (no cache)
 * @property {number} [staleTime] - override default staleTime (default: Infinity)
 * @property {boolean|'always'} [refetchOnMount] - override default refetchOnMount (default: false)
 * @property {boolean} [showErrorMessage] - same as showErrorResult
 * @property {Function} [errorMessageOnCancel]
 * @property {Function} [depKeys] - deps for refetch the api
 */

/** @typedef {BaseQueryFetchConfig & import('./useOnFetch').FetchConfig} QueryFetchConfig */

/**
 * @param {QueryFetchConfig} config
 */
function useFetchQuery({
    retry = 3,
    method = 'POST',
    url,
    onFetchSuccess = null,
    onFetchError = null,
    enabled = null,
    params,
    payload,
    cacheTime,
    staleTime,
    ...rest
}) {
    const fetchData = useOnFetch();

    const _urlKey = useMemo(() => url || '', [url]);
    const _paramsKey = useMemo(() => new URLSearchParams(params).toString(), [params]);
    const _payloadKey = useMemo(() => JSON.stringify(payload || {}), [payload]);

    const getEnabledValue = () => {
        if (typeof enabled === 'function') {
            return Boolean(enabled(payload, params));
        } else if (typeof enabled === 'boolean') {
            return enabled;
        } else {
            return null;
        }
    };

    const _enabled = useMemo(() => getEnabledValue(), [params, payload, enabled]);

    const { data, isFetching, error, refetch } = useQuery({
        queryKey: [_urlKey, _paramsKey, _payloadKey],
        queryFn: () =>
            fetchData({
                url,
                method,
                params,
                data: payload,
                showErrorResult: rest.showErrorMessage,
                onFetchSuccess,
                onFetchError,
                ...rest,
            }),
        refetchOnWindowFocus: false,
        retry,
        ...(_enabled !== null && { enabled: _enabled }),
        staleTime: staleTime !== undefined ? staleTime : 0, // Default: no cache (data langsung stale)
        refetchOnMount: 'always',
        cacheTime: cacheTime !== undefined ? cacheTime : 0, // Default: no cache (cache langsung dihapus)
    });

    return {
        data,
        isLoading: isFetching,
        errMessage: error?.message || null,
        refetch,
        abort: fetchData.abort,
    };
}

export default useFetchQuery;
